import { FunctionComponent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BodyText, Table } from '@gaiads/telia-react-component-library';
import { useTranslation } from 'react-i18next';

import { addEmptyUser, modalClose, modalShow, removeUser, updateUser } from '../../../store/actions';
import UsersListRow from './UsersListRow';
import { RootState } from '../../../store/reducers';
import { User, UserGroup } from '../../../types';
import { InvoicingAccountOptionResponseContents } from '@cdaas/api-clients/dist/asset-user-openapi';

export interface UserGroupNameDropdownOption {
  label: string;
  value: string;
}

type UsersListProps = {
  organizationInvoicingAccounts: InvoicingAccountOptionResponseContents[];
};

const UsersList: FunctionComponent<UsersListProps> = ({ organizationInvoicingAccounts }) => {
  const userList = useSelector((state: RootState) => state.users.users);
  const userGroups: UserGroup[] = useSelector((state: RootState) => state.userGroups.userGroups);
  const userGroupsOptions: UserGroupNameDropdownOption[] = userGroups.map(ug => ({ label: ug.name, value: ug.name }));
  const dispatch = useDispatch();
  const { t } = useTranslation('onboarding');

  const tableHeaders = [
    t('userPage.userNameColumnTitle'),
    t('userPage.userPhoneNumberColumnTitle'),
    t('userPage.userGroupNameColumnTitle'),
    t('userPage.invoicingAccountColumnTitle'),
    t('userPage.employeeIdColumnTitle'),
  ];
  // Add empty column for correct alignment of the header
  tableHeaders.push('');

  const handleInputChange = (
    value: string | null,
    userIndex: number,
    rowKey: 'email' | 'userGroupName' | 'phoneNumber' | 'countryCode' | 'employeeId' | 'invoicingAccount',
  ): void => {
    const modifiedUser: User = { ...userList[userIndex], modified: true };
    if (rowKey === 'email') {
      modifiedUser.email = value!;
    } else if (rowKey === 'userGroupName') {
      const newUserGroup = userGroups.find(ug => ug.name === value);
      modifiedUser.user_group = {
        ...modifiedUser.user_group!,
        name: value!,
        id: newUserGroup!.id,
        default_user_roles: modifiedUser.user_group?.default_user_roles ? [...modifiedUser.user_group.default_user_roles] : [],
      };
      modifiedUser.inherited_invoicing_account = { name: newUserGroup!.invoicing_account?.name || newUserGroup!.inherited_invoicing_account.name };
    } else if (rowKey === 'countryCode') {
      modifiedUser.country_calling_code = value!;
    } else if (rowKey === 'employeeId') {
      modifiedUser.employee_id = value;
    } else if (rowKey === 'invoicingAccount') {
      const invoicingAccount = organizationInvoicingAccounts.find(invoicingAccount => invoicingAccount.id === value);
      modifiedUser.invoicing_account = invoicingAccount ? { ...invoicingAccount, active: true } : null;
    } else {
      modifiedUser.phone_number = value!;
    }
    dispatch(updateUser(modifiedUser));
  };

  const handleDeleteUser = (user: User) => {
    if (!user.email && user.newItem) {
      dispatch(removeUser(user));
      return;
    }

    dispatch(
      modalShow(
        `${t('userPage.deleteUserTitle')}: ${(user.name || user.email) ?? ''} ?`,
        t('userPage.deleteUserYes'),
        t('userPage.deleteUserNo'),
        () => {
          dispatch(removeUser(user));
          dispatch(modalClose());
        },
        () => dispatch(modalClose()),
        undefined,
        undefined,
        true,
      ),
    );
  };

  const handleAddNewUser = () => {
    dispatch(addEmptyUser());
  };

  const AddNewUser = (
    <BodyText clickableAppearance margin={{ left: 'sm', top: 'sm' }} onClick={handleAddNewUser} color={'purple500'}>
      {t('userPage.addNewUserButton')}
    </BodyText>
  );

  return (
    <>
      <Table boxShadow={'sm'} layout={'auto'}>
        {AddNewUser}
        <thead>
          <Table.Row>
            {tableHeaders.map((header, index) => (
              <Table.HeaderCell key={`table-header-${index}`}>
                <BodyText fontWeight="bold">{header}</BodyText>
              </Table.HeaderCell>
            ))}
          </Table.Row>
        </thead>
        <tbody>
          {userList.map((row: User, userIndex) => (
            <UsersListRow
              key={`users-list-row-${userIndex}`}
              user={row}
              userIndex={userIndex}
              userGroupNameDropdownOptions={userGroupsOptions}
              handleInputChange={handleInputChange}
              handleDeleteUser={handleDeleteUser}
              organizationInvoicingAccounts={organizationInvoicingAccounts}
            />
          ))}
        </tbody>
      </Table>
      {AddNewUser}
    </>
  );
};

export default UsersList;
